// File: app/src/components/profile/benchmarkForm.js
//
// The benchmark form's model: what kind of result it is, which fields that
// kind needs, and how effort is asked for each kind. Saved rows keep the
// same shape (benchmarkFromForm in profileSummaries); this only decides
// what the form shows and how an older row loads into it.
//
// Effort is asked in the most concrete terms the kind allows, because vague
// words ("Hard", "Moderate") mean different things to different people and
// three word buttons pull answers to the middle. Lifters judge reps left
// well, so lifts ask "reps left in the tank" as a count. Timed efforts can't
// count reps, so they get three described options. Nothing is preselected.
//
// Pure: no React, no I/O.

import { secondsToClock } from "../../utils/clockInput";
import { effortToRir, isOneRmMetric, rmMetricReps } from "../../utils/benchmarkNormalise";

export const RESULT_TYPES = [
  { value: "lift", label: "Weight for reps", example: "110 kg × 3" },
  { value: "distance", label: "Time for a distance", example: "7:30 for 2000 m" },
  { value: "calories", label: "Calories in a time", example: "20 cal in 1:00" },
  { value: "other", label: "Something else", example: "max reps, watts, a hold" },
];

/** Units for "Something else". */
export const OTHER_UNITS = [
  { value: "reps", label: "reps" },
  { value: "sec", label: "time" },
  { value: "W", label: "watts" },
  { value: "m", label: "metres" },
];

const KIND_FOR_TYPE = { lift: "strength", distance: "cardio", calories: "cardio" };

export const LIFT_EFFORTS = [
  { value: "To failure", label: "Failure", hint: "You couldn't have done another rep." },
  { value: "1 rep left", label: "1", hint: "One more rep, at most." },
  { value: "2 reps left", label: "2", hint: "Two more reps in the tank." },
  { value: "3 reps left", label: "3", hint: "Three more reps in the tank." },
  { value: "4+ reps left", label: "4+", hint: "Four or more left. Further from your limit, so a rougher estimate." },
];

export const PACE_EFFORTS = [
  { value: "Nothing left", label: "Nothing left", hint: "You couldn't have gone any faster or done any more." },
  { value: "A bit left", label: "A bit left", hint: "You could have pushed a little harder." },
  { value: "Held back", label: "Held back", hint: "You eased off on purpose." },
];

export const effortOptions = (type) => (type === "lift" ? LIFT_EFFORTS : PACE_EFFORTS);

/**
 * An effort in this kind's terms. Older words and RPE convert through reps
 * in reserve, rounding towards more left, since people tend to have a little
 * more in the tank than they think. Text it can't read comes back as is.
 */
export const effortForType = (type, raw) => {
  const text = String(raw || "").trim();
  if (!text) return "";
  const options = effortOptions(type);
  if (options.some((o) => o.value === text)) return text;
  const rir = effortToRir(text);
  if (rir == null) return text;
  if (type === "lift") {
    const reps = Math.min(4, Math.ceil(rir - 1e-9));
    return LIFT_EFFORTS[Math.max(0, reps)].value;
  }
  if (rir === 0) return PACE_EFFORTS[0].value;
  return rir < 3 ? PACE_EFFORTS[1].value : PACE_EFFORTS[2].value;
};

/** What kind of result a saved row is. */
export const typeOfCap = (cap) => {
  if (!cap) return "lift";
  const units = [cap.unit, cap.target_unit];
  if (cap.unit === "kg") return "lift";
  if (units.includes("sec") && units.includes("m")) return "distance";
  if (units.includes("sec") && units.includes("cals")) return "calories";
  if (cap.unit === "cals") return "calories";
  if (cap.unit == null && cap.value == null && cap.sec_per_rep != null) return "lift";
  return "other";
};

const num = (raw) => (raw != null && raw !== "" ? String(Number(raw)) : "");

export const EMPTY_FORM = {
  type: "lift",
  label: "",
  metric: "",
  unit: "kg",
  valueNumber: "",
  valueClock: "",
  reps: "",
  forAmount: "",
  forUnit: "m",
  inClock: "",
  secPerRep: "",
  effort: "",
  category: "strength",
  notes: "",
  testedOn: "",
};

/** A saved row as the form, in the shape its kind uses. */
export const formFromCap = (cap) => {
  const type = typeOfCap(cap);
  const base = {
    ...EMPTY_FORM,
    type,
    label: cap.label || "",
    secPerRep: num(cap.sec_per_rep),
    effort: effortForType(type, cap.effort),
    category: cap.category || KIND_FOR_TYPE[type] || null,
    notes: cap.notes || "",
    testedOn: cap.tested_on || "",
  };
  if (type === "lift") {
    // "1RM" and "3 RM" become reps; any other note on the row is kept.
    const reps =
      cap.target_unit === "reps"
        ? num(cap.target_value)
        : isOneRmMetric(cap.metric)
          ? "1"
          : rmMetricReps(cap.metric) != null
            ? String(rmMetricReps(cap.metric))
            : "";
    const rmMetric = isOneRmMetric(cap.metric) || rmMetricReps(cap.metric) != null;
    return { ...base, unit: "kg", valueNumber: num(cap.value), reps, metric: rmMetric ? "" : cap.metric || "" };
  }
  if (type === "distance") {
    const timeFirst = cap.unit === "sec";
    const sec = timeFirst ? cap.value : cap.target_value;
    const metres = timeFirst ? cap.target_value : cap.value;
    return {
      ...base,
      unit: "sec",
      valueClock: sec != null ? secondsToClock(sec, { tenths: true }) : "",
      forAmount: num(metres),
      forUnit: "m",
      metric: cap.metric || "",
    };
  }
  if (type === "calories") {
    const calsFirst = cap.unit === "cals";
    const cals = calsFirst ? cap.value : cap.target_value;
    const sec = calsFirst ? cap.target_value : cap.value;
    return {
      ...base,
      unit: "cals",
      valueNumber: num(cals),
      inClock: sec != null ? secondsToClock(sec, { tenths: true }) : "",
      metric: cap.metric || "",
    };
  }
  const isTime = cap.unit === "sec";
  return {
    ...base,
    unit: cap.unit || "reps",
    valueNumber: isTime ? "" : num(cap.value),
    valueClock: isTime && cap.value != null ? secondsToClock(cap.value, { tenths: true }) : "",
    metric: cap.metric || "",
  };
};

/** A new entry's form. */
export const newForm = (today) => ({ ...EMPTY_FORM, testedOn: today });

/**
 * The form switched to another kind: its fields cleared, effort carried over
 * in the new kind's terms, and Kind following along unless it was set by hand.
 */
export const withType = (form, type) => {
  if (form.type === type) return form;
  const autoKind = !form.category || form.category === KIND_FOR_TYPE[form.type];
  return {
    ...form,
    type,
    unit: type === "lift" ? "kg" : type === "distance" ? "sec" : type === "calories" ? "cals" : "reps",
    valueNumber: "",
    valueClock: "",
    reps: "",
    forAmount: "",
    forUnit: "m",
    inClock: "",
    metric: "",
    effort: effortForType(type, form.effort),
    category: autoKind ? KIND_FOR_TYPE[type] || (form.category === KIND_FOR_TYPE[form.type] ? null : form.category) : form.category,
  };
};

/**
 * The form in the fields benchmarkFromForm reads: a single rep is a tested
 * max ("1RM", no reps), more reps are the target.
 */
export const formForSave = (form) => {
  if (form.type !== "lift") return form;
  const reps = Number(form.reps);
  if (reps === 1) return { ...form, metric: "1RM", reps: "" };
  return form;
};
